import ReactDOM from "react-dom";
import DesignerSetting from "@app/DesignerSetting";
import { ObjectClass } from "@app/types";
import ApplicationOverlay from "@app/widgets/dialogs/ApplicationOverlay";
// This import cannot be shortened without creating circular dependencies.
import { wait } from "./functions/async";
// This import cannot be shortened without creating circular dependencies.
import { maybeYieldToMain } from "./functions/perf";
import ObjectsUsedIn from "./usedIn/ObjectsUsedIn";
const _ = require("lodash");

/**
 * Yield budget for the chunked save-pipeline traversal of prompts.
 */
const PROMPT_CHUNK_SIZE = 5;

/** Pending delayed unmount for @see Utility.closeApplicationOverlay. */
let applicationOverlayCloseTimer = null;

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import {
 *   Design,
 *   DesignCsvData,
 *   DesignFunction,
 *   DesignGraphQlSchema,
 *   DesignIntent,
 *   DesignModule,
 *   DesignModuleLocalVariable,
 *   DesignObject,
 *   DesignVariable
 * } from '@app/types';
 */

/**
 * Static utility methods
 * @module Utility
 */
export class Utility {
    /**
     * Gets the property used to set the default id key for setting up a ChangeSet item.
     * @param {object} item The changeset item
     * @returns {string}
     */
    static getChangeSetIdKey(item) {
        // order is important
        if (!item || typeof item !== "object") {
            return "";
        }
        else if (item.hasOwnProperty("id")) {
            return "id";
        } else if (item.hasOwnProperty("argumentName")) {
            return "argumentName";
        } else if (item.hasOwnProperty("varName")) {
            return "varName";
        }

        return "";
    };

    /**
     * This function is meant to be used on ChangeSet Items (e.g. prompt inputs, outputs, local variables).
     * Checks if both items are objects, if so then checks if both include the same key and value.
     * @param {object} item1
     * @param {object} item2
     * @param {string} id
     * @returns {array<boolean, string>} An array including the result of comparing both values and the key string.
     */
    static getItemsEquality(item1, item2, id) {
        if (!item1
            || !item2
            || typeof item1 !== "object"
            || typeof item2 !== "object"
        ) {
            if (
                item1
                && item2 == null
                && typeof item1 === "object"
                && "id" in item1
                && id
            ) {
                // Some legacy changesets do not have an item2, check ID instead.
                return [item1.id === id, "id"];
            }
            return [false];
        } else if ("id" in item1 && "id" in item2) {
            return [item1.id === item2.id, "id"];
        } else if ("argumentName" in item1 && "argumentName" in item2) {
            return [item1.argumentName === item2.argumentName, "argumentName"];
        } else if ("varName" in item1 && "varName" in item2) {
            return [item1.varName === item2.varName, "varName"];
        }

        return [false];
    };

    /**
     * @param {Design} serialized design to check if it is a common module.
     */
    static getIsCommonModule(serialized){
        return serialized.client === "InteractionsCommon";
    }

    static getTypedValueForStatement(value) {
        try {
            if (value.indexOf("\"") !== -1) {
                return value;
            }
            return JSON.parse(value);
        } catch (e) {
            return "\"" + value + "\"";
        }
    }

    /**
     * Environment-based Non-Collab logic
     * useWindow as false for running this method in non-browser environments (unit tests, etc.)
     */
    static getIsNonCollaboration() {
        return (DesignerSetting.s.is_collab === "false");
    }

    /**
     * Get a component node with ID.
     *
     * @param {string} nodeId
     * @param {string|null} type
     * @param {Design|null} serialized design to get a item from.
     * @returns {DesignCsvData|DesignFunction|DesignGraphQlSchema|DesignIntent|DesignModule|DesignObject|DesignVariable|null}
     */
    static getNode(nodeId, type = null, serialized = null) {
        if (!nodeId) {
            return null;
        }
        if(serialized === null){
            serialized = window.apm.serialized;
        }
        const idFields = (Array.isArray(nodeId)) ? nodeId.split("_") : []; // 0: parent node id; 1: element id
        if (idFields.length === 2) {
            var node = Utility.getNode(idFields[0]);
            var elemIndex = node.versions[node.version].flow.nodes.findIndex(element => element.id === idFields[1]);
            var elem = node.versions[node.version].flow.nodes[elemIndex];
            var refFields = elem.props.ref.split("::"); // 0: refModule id; 1: refModule version
            var refModule = Utility.getNode(refFields[0]);
            var copy = JSON.parse(JSON.stringify(refModule));
            copy.id = refModule.id;
            copy.version = 1;
            copy.changed = false;
            copy.versions[copy.version].flow = refModule.versions[1].flow;
            copy.elemId = nodeId;
            return copy;
        }
        if (type) {
            switch (type) {
                case "function":
                    if (!serialized.functions) {
                        return null;
                    }
                    for (var i = 0; i < serialized.functions.length; i++) {
                        if (nodeId === serialized.functions[i].id) {
                            return serialized.functions[i];
                        }
                    }
                    break;
                case "schema":
                    if (!serialized.graphQLSchemas) {
                        return null;
                    }
                    for (var f = 0; f < serialized.graphQLSchemas.length; f++) {
                        if (nodeId === serialized.graphQLSchemas[f].id) {
                            return serialized.graphQLSchemas[f];
                        }
                    }
                    break;
                case "csvData":
                    if (!serialized.csvData) {
                        return null;
                    }
                    for (var n = 0; n < serialized.csvData.length; n++) {
                        if (nodeId === serialized.csvData[n].id) {
                            return serialized.csvData[n];
                        }
                    }
                    break;
                default:
                    for (var k = 0; k < serialized.components[type + "s"].length; k++) {
                        if (nodeId === serialized.components[type + "s"][k].id) {
                            return serialized.components[type + "s"][k];
                        }
                    }
            }
        } else {
            for (var type in serialized.components) {
                if (serialized.components.hasOwnProperty(type)) {
                    for (var g = 0; g < serialized.components[type].length; g++) {
                        if (nodeId === serialized.components[type][g].id) {
                            return serialized.components[type][g];
                        }
                    }
                }
            }
        }
        return null;
    }

    /**
     * Get a variable
     * @param {string} varIdOrName
     * @param {string|null} parentModuleId If local variable, use module id. If global variable, use null
     * @param {Design|null} serialized design to get variable from.
     * @returns {DesignVariable|DesignModuleLocalVariable|null}
     */
    static getVariable(varIdOrName, parentModuleId = null, serialized = null) {
        // CM TODO: need to look up variable in CM
        var refVars;
        if(serialized === null){
            serialized = window.apm.serialized;
        }
        if (parentModuleId) {
            const parentModule = Utility.getNode(parentModuleId);
            if (parentModule && parentModule.localVariables === undefined) {
                parentModule.localVariables = [];
            }
            refVars = (parentModule) ? parentModule.localVariables : [];
        } else {
            refVars = serialized?.components?.variables ?? [];
        }
        for (var i = 0; i < refVars.length; i++) {
            if (refVars[i].id === varIdOrName || refVars[i].props.config.varName === varIdOrName) {
                return refVars[i];
            }
        }
        return null;
    }
    static getUniqueIntents(intents){
        const comoIntents = intents.filter(int => int.props.moduleApp);
        const nativeIntents = intents.filter(int => !int.props.moduleApp);
        const intentsToUse = [];
        const uniqComoIntentsByName = _.uniqBy(comoIntents, 'name');
        
        uniqComoIntentsByName.forEach(uci => {
            if(nativeIntents.findIndex(ni => ni.name === uci.name) === -1){
                intentsToUse.push(uci);
            }
        });

        return intentsToUse.concat(nativeIntents);
    }

    static getInARowExcludedIntentIds(intents) {
        return intents.filter(intent => !intent.props.config.inARow).map(intent => intent.id);
    }

    static getXAndOutExcludedIntentIds(intents) {
        return intents.filter(intent => !intent.props.config.xAndOut).map(intent => intent.id);
    }

    /**
     * @param {Design|null} serialized design to get play saved prompt nodes from.
     */
    static getPlaySavedPrompts(serialized = null) {
        
        if(serialized === null){
            serialized = window.apm.serialized;
        }

        const nodes = serialized.components.objects;
        var playSavedPrompts = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i]._class === ObjectClass.playSavedPrompt && nodes[i].props.config.key !== "") {

                playSavedPrompts.push({
                    name: nodes[i].props.config.key,
                    value: nodes[i].id
                });
            }
        }
        return playSavedPrompts.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    // DEVDES2-965: Add unused prop to node
    static updateNodePropsUnused(node, unused) {
        // TODO: remove before release
        if (node.hasOwnProperty('unused')) {
            delete node.unused;
        }
        node.props['unused'] = unused;
    }

    /**
     * @param {Design} serialized design to update used state of all prompts within.
     * @param {boolean} [shouldYield] whether process yields to the main thread every @see PROMPT_CHUNK_SIZE.
     */
    static async updatePromptsUsedIn(serialized, shouldYield = false) {
        const sharedIndex = ObjectsUsedIn.buildSharedIndex(serialized);
        const prompts = serialized.components.objects.filter(({ _class }) => _class === ObjectClass.prompt);
        for (const [index, prompt] of prompts.entries()) {
            this._markPromptUsedIn(prompt, serialized, sharedIndex);

            const nextIndex = index + 1;
            if ((nextIndex % PROMPT_CHUNK_SIZE) === 0 && nextIndex < prompts.length) {
                await maybeYieldToMain(shouldYield);
            }
        }
    }

    static _markPromptUsedIn(prompt, serialized, sharedIndex){
        const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(
            prompt.id,
            serialized,
            { 'excludeDisabledInFlow': true },
            sharedIndex
        );
        Utility.updateNodePropsUnused(prompt, promptUsedIn.every(arr => arr.length === 0));
        if (prompt.localVariables) {
            delete prompt.localVariables;
        }
    }

    /**
     * Modifies serialized and export.
     *
     * @param {Design} serialized design to remove duplicate components from.
     */
    static removeDuplicateComponents(serialized) {
        Utility._removeDuplicates(serialized.components.modules);
        Utility._removeDuplicates(serialized.components.objects);
        Utility._removeDuplicates(serialized.components.intents);
        return serialized;
    }

    /**
     * Single forward pass dedup using an id-count map. Preserves the original
     * `removeDuplicateComponents` observable behaviour: same survivor per id
     * (the last occurrence by original index) and same `console.warn` order.
     *
     * @param {{ id: string, toString: () => string }[]} arr mutated in place.
     */
    static _removeDuplicates(arr){
        const counts = new Map();
        for (const item of arr) {
            counts.set(item.id, (counts.get(item.id) || 0) + 1);
        }
        for (let i = 0; i < arr.length; i++) {
            const id = arr[i].id;
            if (counts.get(id) > 1) {
                const dupsFound = arr.splice(i, 1);
                console.warn('duplicate component found! ' + dupsFound[0].toString());
                counts.set(id, counts.get(id) - 1);
                i--;
            }
        }
    }

    /**
     * Filter out potentially harmful keywords
     * @param statement
     */
    static filterEval(statement) {
        const filter = /(this|iframe|require|exec|WScript|script|document|window|alert|XMLHttpRequest|CustomEvent|\$|jQuery|w2ui|w2popup|React|function|=>|while|for|apm|colm)/ig;
        return statement.replace(filter, "");
    }

    /**
     * Evaluate the js statement; presumably a JS statement that can be evaluate to true or false
     * @param statement
     * @returns {boolean}
     */
    static evaluateStatement(statement) {
        const filteredStatement = Utility.filterEval(statement);
        try {
            const evaluation = eval(filteredStatement);
            return evaluation ? true : false;
        } catch (e) {
            return false;
        }
    }

    /**
     * Change new line character to <br/> tag
     * @param {string} str
     * @returns {Array|string}
     */
    static newlineToBr(str) {
        const newlineRegex = /(\r\n|\r|\n)/g;

        if (typeof str === 'number') {
            return str;
        } else if (typeof str !== 'string') {
            return '';
        }

        return str.split(newlineRegex).map(function (line, index) {
            if (line.match(newlineRegex)) {
                return <br key={index}/>;
            }
            return line;
        });
    }

    /**
     * Calculate control point coordinate on a single axis
     * @param {Array} K Coordinates
     * @returns {{p1: Array, p2: Array}}
     */
    static getControlPoints(K, W) {
        var p1 = [];
        var p2 = [];
        var n = K.length - 1;

        /*rhs vector*/
        var a = [];
        var b = [];
        var c = [];
        var d = [];
        var r = [];

        /*left most segment*/
        a[0] = 0; // outside the matrix
        b[0] = 2;
        c[0] = -1;
        d[0] = 0;
        r[0] = K[0];// add curvature at K0

        /*internal segments*/
        for (i = 1; i < n; i++) {
            a[2 * i - 1] = 1 * W[i] * W[i];
            b[2 * i - 1] = -2 * W[i] * W[i];
            c[2 * i - 1] = 2 * W[i - 1] * W[i - 1];
            d[2 * i - 1] = -1 * W[i - 1] * W[i - 1];
            r[2 * i - 1] = K[i] * ((-W[i] * W[i] + W[i - 1] * W[i - 1]));

            a[2 * i] = W[i];
            b[2 * i] = W[i - 1];
            c[2 * i] = 0;
            d[2 * i] = 0; // note: d[2n-2] is already outside the matrix
            r[2 * i] = (W[i - 1] + W[i]) * K[i];

        }

        /*right segment*/
        a[2 * n - 1] = -1;
        b[2 * n - 1] = 2;
        r[2 * n - 1] = K[n];// curvature at last point

        // the following array elements are not in the original matrix, so they should not be used:
        c[2 * n - 1] = 0; // outside the matrix
        d[2 * n - 2] = 0; // outside the matrix
        d[2 * n - 1] = 0; // outside the matrix

        /*solves Ax=b with the Thomas algorithm (from Wikipedia)*/
        var p = Utility.Thomas4(r, a, b, c, d);

        /*re-arrange the array*/
        for (var i = 0; i < n; i++) {
            p1[i] = p[2 * i];
            p2[i] = p[2 * i + 1];
        }

        return {p1: p1, p2: p2};
    }

    static Thomas4(r, a, b, c, d) {
        var n = r.length;
        var p = [];

        // the following array elements are not in the original matrix, so they should not have an effect
        a[0] = 0; // outside the matrix
        c[n - 1] = 0; // outside the matrix
        d[n - 2] = 0; // outside the matrix
        d[n - 1] = 0; // outside the matrix

        /*solves Ax=b with the Thomas algorithm (from Wikipedia)*/
        /* adapted for a 4-diagonal matrix. only the a[i] are under the diagonal, so the Gaussian elimination is very similar */
        for (var i = 1; i < n; i++) {
            var m = a[i] / b[i - 1];
            b[i] = b[i] - m * c[i - 1];
            c[i] = c[i] - m * d[i - 1];
            r[i] = r[i] - m * r[i - 1];
        }

        p[n - 1] = r[n - 1] / b[n - 1];
        p[n - 2] = (r[n - 2] - c[n - 2] * p[n - 1]) / b[n - 2];
        for (var i = n - 3; i >= 0; --i) {
            p[i] = (r[i] - c[i] * p[i + 1] - d[i] * p[i + 2]) / b[i];
        }
        return p;
    }

    /**
     * Get multi-segment bezier curve for given link and points
     * @param {Object} link Link model
     * @returns {Array}
     */
    static getCurve(link) {
        const points = link.points;
        var segments = [];
        if (points.length === 2) {
            var stu = {x: points[1].x - points[0].x, y: points[1].y - points[0].y};
            const stuMag = Math.sqrt(stu.x * stu.x + stu.y * stu.y);
            stu.x /= stuMag !== 0 ? stuMag : 1;
            stu.y /= stuMag !== 0 ? stuMag : 1;
            const sv = link.getSourcePort() ? link.getSourcePort().unitVector : stu;
            const tv = link.getTargetPort() ? link.getTargetPort().unitVector : {x: -stu.x, y: -stu.y};
            const magX = Math.abs((points[0].x - points[1].x) / 2);
            const magY = Math.abs((points[0].y - points[1].y) / 2);
            segments.push([
                points[0],
                {x: points[0].x + sv.x * magX, y: points[0].y + sv.y * magY},
                {x: points[1].x + tv.x * magX, y: points[1].y + tv.y * magY},
                points[1]
            ]);
        } else if (points.length > 2) {
            var xs = [];
            var ys = [];
            for (var i = 0; i < points.length; i++) {
                xs.push(points[i].x);
                ys.push(points[i].y);
            }
            var weights = [];
            for (i = 0; i < points.length; i++) {
                /* calculate Euclidean distance */
                weights[i] = Math.sqrt(Math.pow((xs[i + 1] - xs[i]), 2) + Math.pow((ys[i + 1] - ys[i]), 2));
                // if the weight is too small, the calculation becomes instable
                weights[i] = 1 < weights[i] ? weights[i] : 1;
            }
            weights[points.length] = weights[points.length - 1];
            var cx = Utility.getControlPoints(xs, weights);
            var cy = Utility.getControlPoints(ys, weights);

            for (var i = 0; i < points.length - 1; i++) {
                segments.push([
                    {x: points[i].x, y: points[i].y},
                    {x: cx.p1[i], y: cy.p1[i]},
                    {x: cx.p2[i], y: cy.p2[i]},
                    {x: points[i + 1].x, y: points[i + 1].y}
                ]);
            }
        }
        return segments;
    };

    /**
     * Get bezier coordinates given control points and position on curve
     * @param {Object} p0 Curve start point
     * @param {Object} p1 Control point 1
     * @param {Object} p2 Control point 2
     * @param {Object} p3 Curve end point
     * @param {number} t Coefficient
     * @returns {Object} Point on the curve
     */
    static getBezierCoord(p0, p1, p2, p3, t) {
        var ret = {};
        const coords = ['x', 'y'];
        const oneMinusT = 1 - t;
        const oneMinusTSq = Math.pow(oneMinusT, 2);
        const TSq = Math.pow(t, 2);
        const coeff1 = oneMinusTSq * oneMinusT;
        const coeff2 = 3 * oneMinusTSq * t;
        const coeff3 = 3 * oneMinusT * TSq;
        const coeff4 = TSq * t;
        for (var i in coords) {
            // noinspection JSUnfilteredForInLoop
            // No problem since coords is an array
            const k = coords[i];
            ret[k] = coeff1 * p0[k] + coeff2 * p1[k] + coeff3 * p2[k] + coeff4 * p3[k];
        }
        return ret;
    }

    /**
     * Convert a serialized node to tree format
     * @param node in serialized format
     * @param isHidden hide the node if set to true
     */
    static getTreeNode(node, isHidden = false) {
        return {
            icon: node.props?.iconClass ?? "",
            id: node.id,
            text: node.name,
            type: node.type,
            state: {hidden: isHidden},
            data: { _class: node._class },
            props: node.props,
            li_attr: { class : (node.props.config.disabledInFlow) ? 'disabledInFlow' : '' }
        };
    }

    static isObject(item) {
        return (item && typeof item === 'object' && !Array.isArray(item) && item !== null);
    }

    /**
     * Deep merge two objects.
     * @param target
     * @param source
     */
    static mergeDeep(target, source) {
        if (this.isObject(target) && this.isObject(source)) {
            for (const key in source) {
                if (this.isObject(source[key])) {
                    if (!target[key]) Object.assign(target, {[key]: {}});
                    this.mergeDeep(target[key], source[key]);
                } else {
                    Object.assign(target, {[key]: source[key]});
                }
            }
        }
        return target;
    }

    static parseBool(value) {
        return !!value && value !== "false";
    }

    /** sets $loki and meta on serialized to syncronize with localStorage */
    static setLokiAndMetaOnDesignVersion({targetVersion, sourceVersion}){
        targetVersion["$loki"] = sourceVersion["$loki"];
        targetVersion["meta"] = sourceVersion["meta"];
    
        return targetVersion;
    }

    static openApplicationOverlay(message, opts){
        const hideSpinner = (opts) ? opts.hideSpinner : false;

        if (applicationOverlayCloseTimer != null) {
            clearTimeout(applicationOverlayCloseTimer);
            applicationOverlayCloseTimer = null;
        }
        
        ReactDOM.render(<ApplicationOverlay 
            message={message} 
            hideSpinner={hideSpinner}/>, $('#app-overlay').get(0));
    }
    
    static closeApplicationOverlay(ms = 750){
        if (applicationOverlayCloseTimer != null) {
            clearTimeout(applicationOverlayCloseTimer);
            applicationOverlayCloseTimer = null;
        }

        const unmount = () => {
            applicationOverlayCloseTimer = null;
            ReactDOM.unmountComponentAtNode($('#app-overlay').get(0));
        };
        if (ms <= 0) {
            unmount();
            return;
        }
        applicationOverlayCloseTimer = setTimeout(unmount, ms);
    }

    static getChangeLogLabel(key) {
        const labelsMap = {
            modules: 'Modules',
            'application-modules': 'Application Modules',
            'application-objects': 'Application Objects',
            'application-variables': 'Application Variables',
            'application-intents': 'Application Intents',
            globalIntents: 'Global Intents',
            compoundIntents: 'Compound Intents',
            languages: 'Languages',
            applicationVersion: 'Dependency Version',
            functions: 'Functions',
            fillerPrompts: 'Filler Prompts',
            graphQLSchemas: 'GraphQLSchemas',
            csvData: 'CSV Data (Tabular Data)',
            ccxml: 'CCXML',
            settings: 'Settings',
            appProperties: 'App Properties',
            unitTests: 'Unit Tests',
            intentHandling: 'Intent Handling',
            folders: 'Module Folders',
      
            // settings
            globalErrorHandling: 'Global Error Handling',
            globalExceptionHandling: 'Global Exception Handling',
            description: 'Description',
            noInputTimeout: 'No Input Timeout global setting',
            dtmf_live_agent_override_pattern: 'Live Agent DTMF Override Pattern',
      
            // intentHandling
            inARowLimit: 'Intent Handling: In A Row Limit',
            xAndOutLimit: 'Intent Handling: X And Out Limit',
      
            //globalLoopHandling
            inARow: 'Global Loop Handling: In A Row',
            xAndOut: 'Global Loop Handling: X And Out',
      
            // omniChannel
            omniChannel: 'Omni Channel',
      
            // sensitiveOverrides
            sensitiveOverrides: 'Sensitive Overrides',
          }
      
          return labelsMap[key] || key
    }
    
    /**
     * Add a new node to the application panel and to `window.apm`.
     * @param {string} parentNode The parent node.
     * @param {object} node The node to be added.
     * @param {string} position The new node position.
     * @param {function|undefined} callback A function to be called once the node is added.
     */
    static addNodeToTreeDomElement(parentNode, node, position, callback = () => {}) {
        const treeDomElement = $('#tl_container');
        if (!treeDomElement) return;

        treeDomElement.jstree().create_node(parentNode, Utility.getTreeNode(node), position, callback);
    }

    /**
     * Focus the first input on the actual property panel.
    */ 
    static async focusPropertyPanelInput() {
        // Wait the transition time set in resizeable panel CSS.
        await wait(300);
        let lastOffset = null;
        const interval = setInterval(() => {
            const currentOffset = $("#right-panels")[0].offsetLeft;
            if (currentOffset !== lastOffset) {
                // Wait for the panel to stop moving befor focusing.
                lastOffset = currentOffset;
                return;
            }

            const input = $("#property_panel_props").find('input, textarea')[0];
            if (input) {
                input.focus();
                input.select();
                clearInterval(interval);
            }
        }, 50);
    }

    /**
     * Hide property panel
     */
    static hidePropertyPanel() {
        const panel = $("#property_panel");
        $("#right-panels").css("right", "-500px");
        // Use reference so an uninitialized tree is not created with defaults.
        $.jstree.reference("#tl_container")?.deselect_all();
        return ReactDOM.unmountComponentAtNode(panel.get(0));
    }
}

export default Utility;
